/**
 * AdminSaaSPricingPage
 * [UC-019] SaaS Pricing Configuration
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import { fmt, fmtMoney } from '../utils/format';

interface PricingForm {
  monthlyPlanPrice: string;
  yearlyPlanPrice: string;
}

interface PricingConfiguration {
  saaSPricingConfigId: number | null;
  monthlyPlanPrice: number;
  yearlyPlanPrice: number;
}

interface ValidationErrors {
  monthlyPlanPrice?: string;
  yearlyPlanPrice?: string;
}

export function AdminSaaSPricingPage() {
  const [form, setForm] = useState<PricingForm>({
    monthlyPlanPrice: '',
    yearlyPlanPrice: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentPricing, setCurrentPricing] = useState<PricingConfiguration>({
    saaSPricingConfigId: null,
    monthlyPlanPrice: 0,
    yearlyPlanPrice: 0
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Load current pricing configuration on mount
  useEffect(() => {
    loadCurrentPricing();
  }, []);

  const loadCurrentPricing = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<PricingConfiguration>('/saas-pricing-config');
      setCurrentPricing(response);
    } catch (error) {
      console.error('Failed to load pricing configuration:', error);
      // Handle 404 (no config yet) as empty state
      if ((error as any)?.status === 404) {
        setCurrentPricing({
          saaSPricingConfigId: null,
          monthlyPlanPrice: 0,
          yearlyPlanPrice: 0
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate monthly savings for preview
  const monthlySavings = useMemo(() => {
    const monthly = parseFloat(form.monthlyPlanPrice) || 0;
    const yearly = parseFloat(form.yearlyPlanPrice) || 0;
    if (monthly > 0 && yearly > 0) {
      return (monthly * 12) - yearly;
    }
    return 0;
  }, [form.monthlyPlanPrice, form.yearlyPlanPrice]);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Validate Monthly Plan Price
    const monthlyPrice = parseFloat(form.monthlyPlanPrice);
    if (!form.monthlyPlanPrice || isNaN(monthlyPrice) || monthlyPrice <= 0) {
      newErrors.monthlyPlanPrice = t('adminSaaSPricing.errorMonthlyPrice', 'Monthly plan price must be a positive number');
      isValid = false;
    }

    // Validate Yearly Plan Price
    const yearlyPrice = parseFloat(form.yearlyPlanPrice);
    if (!form.yearlyPlanPrice || isNaN(yearlyPrice) || yearlyPrice <= 0) {
      newErrors.yearlyPlanPrice = t('adminSaaSPricing.errorYearlyPrice', 'Yearly plan price must be a positive number');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const pricingData = {
        monthlyPlanPrice: parseFloat(form.monthlyPlanPrice),
        yearlyPlanPrice: parseFloat(form.yearlyPlanPrice)
      };

      const response = await api.put<PricingConfiguration>('/saas-pricing-config', pricingData);

      // Update current pricing from response
      setCurrentPricing(response);

      // Show success message
      setShowSuccessMessage(true);

      // Reset form
      setForm({
        monthlyPlanPrice: '',
        yearlyPlanPrice: ''
      });
      setErrors({});

    } catch (error: any) {
      console.error('Failed to save pricing configuration:', error);

      // Handle validation errors (400)
      if (error?.response?.status === 400) {
        const apiErrors = error.response.data?.errors || {};
        setErrors(apiErrors);
      } else {
        // Show generic error for 403/500
        setErrors({
          monthlyPlanPrice: t('adminSaaSPricing.errorSave', 'Failed to update pricing. Please try again.')
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form and errors
    setForm({
      monthlyPlanPrice: '',
      yearlyPlanPrice: ''
    });
    setErrors({});

    // Navigate back to admin dashboard
    navigate('/admin/dashboard');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">{t('adminSaaSPricing.title', 'SaaS Pricing Configuration')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminSaaSPricing.subtitle', 'Configure monthly and yearly subscription pricing for your SaaS platform')}</p>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-green-600/15 border border-green-600/25 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-base sm:text-sm font-medium text-green-700 dark:text-green-500">
              {t('adminSaaSPricing.successMessage', 'Pricing configuration updated successfully')}
            </span>
          </div>
        </div>
      )}

      {/* SaaS Pricing Form */}
      <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-5 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">{t('adminSaaSPricing.formTitle', 'Subscription Pricing')}</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Monthly Plan Price */}
          <div>
            <label htmlFor="monthlyPrice" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
              {t('adminSaaSPricing.monthlyLabel', 'Monthly Plan Price')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-[var(--text-muted)] text-base sm:text-sm">€</span>
              </div>
              <input
                type="number"
                id="monthlyPrice"
                value={form.monthlyPlanPrice}
                onChange={(e) => setForm(prev => ({ ...prev, monthlyPlanPrice: e.target.value }))}
                step="0.01"
                min="0"
                className={`w-full pl-8 pr-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-base sm:text-sm ${
                  errors.monthlyPlanPrice ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder="29.99"
              />
            </div>
            {errors.monthlyPlanPrice && (
              <p className="mt-1.5 text-sm text-red-600">{errors.monthlyPlanPrice}</p>
            )}
          </div>

          {/* Yearly Plan Price */}
          <div>
            <label htmlFor="yearlyPrice" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
              {t('adminSaaSPricing.yearlyLabel', 'Yearly Plan Price')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-[var(--text-muted)] text-base sm:text-sm">€</span>
              </div>
              <input
                type="number"
                id="yearlyPrice"
                value={form.yearlyPlanPrice}
                onChange={(e) => setForm(prev => ({ ...prev, yearlyPlanPrice: e.target.value }))}
                step="0.01"
                min="0"
                className={`w-full pl-8 pr-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-base sm:text-sm ${
                  errors.yearlyPlanPrice ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder="299.99"
              />
            </div>
            {errors.yearlyPlanPrice && (
              <p className="mt-1.5 text-sm text-red-600">{errors.yearlyPlanPrice}</p>
            )}
          </div>

          {/* Pricing Preview */}
          {(form.monthlyPlanPrice || form.yearlyPlanPrice) && (
            <div className="p-4 bg-[var(--surface-raised)] rounded-lg border border-[var(--border-subtle)]">
              <h3 className="text-base sm:text-sm font-medium text-[var(--text-primary)] mb-3">
                {t('adminSaaSPricing.previewTitle', 'Pricing Preview')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {form.monthlyPlanPrice && (
                  <div className="text-center p-3 bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)]">
                    <div className="text-lg font-semibold text-[var(--text-primary)]">€{form.monthlyPlanPrice}</div>
                    <div className="text-sm text-[var(--text-muted)]">{t('adminSaaSPricing.perMonth', 'per month')}</div>
                  </div>
                )}
                {form.yearlyPlanPrice && (
                  <div className="text-center p-3 bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)]">
                    <div className="text-lg font-semibold text-[var(--text-primary)]">€{form.yearlyPlanPrice}</div>
                    <div className="text-sm text-[var(--text-muted)]">{t('adminSaaSPricing.perYear', 'per year')}</div>
                    {monthlySavings > 0 && (
                      <div className="text-xs text-green-600 mt-1">
                        {t('adminSaaSPricing.savePerMonth', { amount: fmtMoney(monthlySavings, 2), defaultValue: `Save ${fmtMoney(monthlySavings, 2)}/month` })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('adminSaaSPricing.saving', 'Saving...') : t('adminSaaSPricing.savePricing', 'Save Pricing')}
            </button>
          </div>
        </form>
      </div>

      {/* Current Configuration */}
      <div className="mt-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-5 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
          {t('adminSaaSPricing.currentConfig', 'Current Configuration')}
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface-raised)] rounded-lg border border-[var(--border-subtle)]">
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">
                  {t('adminSaaSPricing.monthlyPlan', 'Monthly Plan')}
                </div>
                <div className="text-xl font-bold text-[var(--text-primary)]">
                  {fmtMoney(currentPricing.monthlyPlanPrice, 2)}
                </div>
              </div>
              <div className="p-4 bg-[var(--surface-raised)] rounded-lg border border-[var(--border-subtle)]">
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">
                  {t('adminSaaSPricing.yearlyPlan', 'Yearly Plan')}
                </div>
                <div className="text-xl font-bold text-[var(--text-primary)]">
                  {fmtMoney(currentPricing.yearlyPlanPrice, 2)}
                </div>
              </div>
            </div>
            {currentPricing.saaSPricingConfigId && (
              <div className="mt-4 text-sm text-[var(--text-muted)]">
                {t('adminSaaSPricing.lastUpdated', 'Last updated: Configuration available')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminSaaSPricingPage;
